import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import * as React from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { SignerInput } from "../../../api/ui";
import { createSignatureUi } from "../../../api/ui";
import { useUiAuth } from "../../../context/UiAuthContext";

interface ValidationErrorObject {
  name?: string;
  userMessage?: string;
  message?: string;
}

interface BackendErrorData {
  objects?: ValidationErrorObject[];
  message?: string;
  userMessage?: string;
  detailedMessage?: string;
}

interface LocalSignerInput extends SignerInput {
  id: string;
}

export function AssinaturasCreate(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUiAuth();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorEmail, setAuthorEmail] = useState("");
  const [authorDocument, setAuthorDocument] = useState("");
  const tenant = "";
  const [file, setFile] = useState<File | null>(null);

  const [signers, setSigners] = useState<LocalSignerInput[]>([
    { id: "signer-0", name: "", email: "", document: "", signatureType: "PARAFE" },
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      if (!authorName && user.name) setAuthorName(user.name);
      if (!authorEmail && user.email) setAuthorEmail(user.email);
    }
  }, [authorEmail, authorName, user]);

  const handleAddSigner = (): void => {
    setSigners([...signers, { id: `signer-${Date.now()}-${signers.length}`, name: "", email: "", document: "", signatureType: "PARAFE" }]);
  };

  const handleRemoveSigner = (index: number): void => {
    setSigners(signers.filter((_, i) => i !== index));
  };

  const handleSignerChange = (index: number, field: keyof SignerInput, value: string): void => {
    const updated = [...signers];
    updated[index] = { ...updated[index], [field]: value };
    setSigners(updated);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handlePreviewFile = (): void => {
    if (file) {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!title || !authorName || !authorEmail || !authorDocument || !file) {
      setError(t("assinaturas.create.requiredError"));
      return;
    }

    if (signers.length === 0) {
      setError(t("assinaturas.create.noSigners"));
      return;
    }

    for (let i = 0; i < signers.length; i++) {
      if (!signers[i].name || !signers[i].email || !signers[i].document) {
        setError(t("assinaturas.create.signerRequiredError", { index: i + 1 }));
        return;
      }
    }

    setLoading(true);

    try {
      let formattedDueDate: string | undefined;
      if (dueDate) {
        formattedDueDate = `${dueDate}T23:59:59Z`;
      }

      await createSignatureUi({
        title,
        description,
        authorName,
        authorEmail,
        authorDocument,
        tenant,
        file,
        signers,
        sendLinkAndOtp: true,
        signatureDueDate: formattedDueDate,
      });

      navigate("/assinaturas");
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: BackendErrorData | string }; message?: string };
      const data = errorObj.response?.data;
      let errorMsg = t("assinaturas.create.createError");
      if (data && typeof data === "object") {
        if (data.objects && Array.isArray(data.objects) && data.objects.length > 0) {
          errorMsg = data.objects
            .map((o: ValidationErrorObject) => o.userMessage || o.message || `${o.name || ""}: erro de validação`)
            .join("; ");
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.userMessage) {
          errorMsg = data.userMessage;
        } else if (data.detailedMessage) {
          errorMsg = data.detailedMessage;
        }
      } else if (typeof data === "string") {
        errorMsg = data;
      } else if (errorObj.message) {
        errorMsg = errorObj.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={(e) => { void handleSubmit(e); }} sx={{ maxWidth: 1200, mx: "auto", pb: 6 }}>
      {/* Título e Subtítulo fora do card */}
      <Box sx={{ mb: 3 }}>
        <Typography color="#1e293b" component="h1" fontWeight="bold" gutterBottom variant="h4">
          {t("assinaturas.create.title")}
        </Typography>
        <Typography color="text.secondary" variant="body1">
          {t("assinaturas.create.subtitle")}
        </Typography>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert> : null}

      {/* Card principal do formulário */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Seção 1: Dados do Envelope */}
        <Typography color="#1e293b" fontWeight="bold" sx={{ mb: 3 }} variant="h6">
          {t("assinaturas.create.envelopeData")}
        </Typography>

        <TextField
          fullWidth
          label={t("assinaturas.create.envelopeName")}
          onChange={(e) => { setTitle(e.target.value); }}
          required
          sx={{ mb: 3 }}
          value={title}
        />

        <TextField
          fullWidth
          label={t("assinaturas.create.description")}
          multiline
          onChange={(e) => { setDescription(e.target.value); }}
          rows={2}
          sx={{ mb: 3 }}
          value={description}
        />

        <TextField
          InputLabelProps={{ shrink: true }}
          fullWidth
          helperText={t("assinaturas.create.dueDateHelper")}
          label={t("assinaturas.create.dueDate")}
          onChange={(e) => { setDueDate(e.target.value); }}
          sx={{ mb: 3.5 }}
          type="date"
          value={dueDate}
        />

        <Divider sx={{ my: 4 }} />

        {/* Dados do Autor / Solicitante */}
        <Typography color="#1e293b" fontWeight="bold" sx={{ mb: 3 }} variant="h6">
          {t("assinaturas.create.authorData")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2.5, width: "100%", mb: 2 }}>
          <TextField
            fullWidth
            label={t("assinaturas.create.authorName")}
            onChange={(e) => { setAuthorName(e.target.value); }}
            required
            sx={{ flex: 1 }}
            value={authorName}
          />
          <TextField
            fullWidth
            label={t("assinaturas.create.authorEmail")}
            onChange={(e) => { setAuthorEmail(e.target.value); }}
            required
            sx={{ flex: 1 }}
            type="email"
            value={authorEmail}
          />
          <TextField
            fullWidth
            label={t("assinaturas.create.authorDocument")}
            onChange={(e) => { setAuthorDocument(e.target.value); }}
            required
            sx={{ flex: 1 }}
            value={authorDocument}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Seção 2: Documentos */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <Typography color="#1e293b" fontWeight="bold" variant="h6">
            {t("assinaturas.create.documents")}
          </Typography>

          <Button
            component="label"
            startIcon={<AddIcon />}
            sx={{
              color: "#2F16C9",
              borderColor: "#2F16C9",
              fontWeight: "bold",
              textTransform: "uppercase",
              fontSize: "0.85rem",
              borderRadius: 1.5,
              px: 2,
              "&:hover": { borderColor: "#0C1C69", bgcolor: "#eeebfc" },
            }}
            variant="outlined"
          >
            {t("assinaturas.create.addDocument")}
            <input accept="application/pdf" hidden onChange={handleFileChange} type="file" />
          </Button>
        </Box>

        {file ? (
          <Paper
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 1.5,
              borderColor: "#e2e8f0",
              mb: 3,
            }}
            variant="outlined"
          >
            <Typography color="#334155" fontWeight="500" variant="body1">
              {file.name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title={t("assinaturas.create.previewDocument")}>
                <IconButton color="primary" onClick={handlePreviewFile} size="small">
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("assinaturas.create.removeDocument")}>
                <IconButton onClick={() => { setFile(null); }} size="small" sx={{ color: "#ef4444" }}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        ) : (
          <Typography color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }} variant="body2">
            {t("assinaturas.create.noDocuments")}
          </Typography>
        )}

        <Divider sx={{ my: 4 }} />

        {/* Seção 3: Signatários */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <Typography color="#1e293b" fontWeight="bold" variant="h6">
            {t("assinaturas.create.signers")}
          </Typography>

          <Button
            onClick={handleAddSigner}
            startIcon={<AddIcon />}
            sx={{
              color: "#2F16C9",
              borderColor: "#2F16C9",
              fontWeight: "bold",
              textTransform: "uppercase",
              fontSize: "0.85rem",
              borderRadius: 1.5,
              px: 2,
              "&:hover": { borderColor: "#0C1C69", bgcolor: "#eeebfc" },
            }}
            variant="outlined"
          >
            {t("assinaturas.create.newSigner")}
          </Button>
        </Box>

        {signers.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }} variant="body2">
            {t("assinaturas.create.noSigners")}
          </Typography>
        ) : (
          signers.map((signer, index) => (
            <Box
              key={signer.id}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: 2.5,
                width: "100%",
                mb: 2.5,
              }}
            >
              <TextField
                fullWidth
                label={t("assinaturas.create.fullName")}
                onChange={(e) => { handleSignerChange(index, "name", e.target.value); }}
                required
                size="small"
                sx={{ flex: 1 }}
                value={signer.name}
              />

              <TextField
                fullWidth
                label={t("assinaturas.create.document")}
                onChange={(e) => { handleSignerChange(index, "document", e.target.value); }}
                required
                size="small"
                sx={{ flex: 1 }}
                value={signer.document}
              />

              <TextField
                fullWidth
                label={t("assinaturas.create.email")}
                onChange={(e) => { handleSignerChange(index, "email", e.target.value); }}
                required
                size="small"
                sx={{ flex: 1 }}
                type="email"
                value={signer.email}
              />

              <IconButton
                onClick={() => { handleRemoveSigner(index); }}
                size="small"
                sx={{ color: "#ef4444", flexShrink: 0, alignSelf: { xs: "flex-end", md: "center" } }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))
        )}

        {/* Botões de Submissão */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
          <Button color="inherit" onClick={() => { navigate("/assinaturas"); }}>
            {t("assinaturas.create.cancel")}
          </Button>
          <Button
            disabled={loading}
            sx={{
              bgcolor: "#2F16C9",
              fontWeight: "bold",
              minWidth: 160,
              borderRadius: 1.5,
              "&:hover": { bgcolor: "#0C1C69" },
            }}
            type="submit"
            variant="contained"
          >
            {loading ? <CircularProgress color="inherit" size={24} /> : t("assinaturas.create.submit")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AssinaturasCreate;
